<template>
  <div class="image-capture">

    <!-- Camera feed -->
    <div class="image-capture__camera">
      <video
        ref="cameraFeed"
        class="image-capture__video"
        autoplay
        playsinline
        muted
      />
      <canvas ref="captureCanvas" class="d-none" />

      <div class="image-capture__controls">
        <v-btn
          fab color="white" large
          @click="capture"
          :disabled="!camera_active"
        >
          <v-icon color="grey darken-3" large>mdi-camera</v-icon>
        </v-btn>
      </div>

      <div class="image-capture__status" v-if="!camera_active">
        <v-progress-circular indeterminate color="white" size="24" v-if="camera_loading" />
        <div v-if="camera_error" class="text-center" style="opacity:0.7;font-size:13px;">
          <v-icon dark>mdi-camera-off</v-icon>
          <div class="mt-2">{{ camera_error }}</div>
        </div>
      </div>
    </div>

    <!-- Photo gallery -->
    <div v-if="photos.length" class="image-capture__gallery pa-3">
      <div class="d-flex align-center justify-space-between mb-2">
        <span class="text-overline" style="opacity:0.7">Fotos ({{ photos.length }})</span>
        <v-btn icon x-small dark @click="clearAll">
          <v-icon x-small>mdi-delete-sweep</v-icon>
        </v-btn>
      </div>
      <div class="image-capture__grid">
        <div
          v-for="(photo, i) in photos"
          :key="i"
          class="image-capture__thumb"
          @click="preview_index = i"
        >
          <img :src="photo.dataUrl" />
          <v-btn
            icon x-small
            class="image-capture__thumb-remove"
            @click.stop="remove(i)"
          >
            <v-icon x-small color="white">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <div v-else class="text-center pa-6" style="opacity:0.5">
      <v-icon size="32" color="grey darken-1">mdi-image-multiple-outline</v-icon>
      <div class="mt-1" style="font-size:13px;">Noch keine Fotos aufgenommen</div>
    </div>

    <!-- Preview overlay -->
    <v-overlay :value="preview_index !== null" z-index="100" @click.native="preview_index = null">
      <div v-if="preview_index !== null" @click.stop style="position:relative;max-width:90vw;max-height:80vh;">
        <img :src="photos[preview_index].dataUrl" style="max-width:100%;max-height:80vh;border-radius:8px;" />
        <v-btn icon dark style="position:absolute;top:8px;right:8px;background:rgba(0,0,0,0.5)!important;" @click="preview_index = null">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </v-overlay>

  </div>
</template>

<script>
export default {
  name: 'ImageCapture',

  props: {
    value: {
      type: Array,
      default: () => [],
    },
    autostart: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      camera_active: false,
      camera_loading: false,
      camera_error: null,
      media_stream: null,
      preview_index: null,
    }
  },

  computed: {
    photos: {
      get() { return this.value },
      set(val) { this.$emit('input', val) },
    },
  },

  mounted() {
    if (this.autostart) this.startCamera()
  },

  beforeDestroy() {
    this.stopCamera()
  },

  methods: {
    async startCamera() {
      this.camera_loading = true
      this.camera_error = null
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        })
        this.media_stream = stream
        this.$refs.cameraFeed.srcObject = stream
        this.camera_active = true
      } catch (e) {
        this.camera_error = 'Kamera konnte nicht gestartet werden. Bitte Berechtigung prüfen.'
      } finally {
        this.camera_loading = false
      }
    },

    stopCamera() {
      if (this.media_stream) {
        this.media_stream.getTracks().forEach(t => t.stop())
        this.media_stream = null
      }
      this.camera_active = false
    },

    capture() {
      const video = this.$refs.cameraFeed
      const canvas = this.$refs.captureCanvas

      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      canvas.getContext('2d').drawImage(video, 0, 0)

      const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
      this.photos = [...this.photos, { dataUrl, timestamp: Date.now() }]
      this.$emit('capture', this.photos[this.photos.length - 1])
    },

    remove(index) {
      const updated = [...this.photos]
      updated.splice(index, 1)
      this.photos = updated
      if (this.preview_index === index) this.preview_index = null
      else if (this.preview_index !== null && this.preview_index > index) this.preview_index--
    },

    clearAll() {
      this.photos = []
      this.preview_index = null
    },
  },
}
</script>

<style lang="scss" scoped>
.image-capture {
  display: flex;
  flex-direction: column;

  &__camera {
    position: relative;
    flex-shrink: 0;
    background: #000;
    aspect-ratio: 4 / 3;
    max-height: 50vh;
    overflow: hidden;
  }

  &__video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__controls {
    position: absolute;
    bottom: 16px;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
  }

  &__status {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    background: rgba(0, 0, 0, 0.6);
  }

  &__gallery {
    flex-shrink: 0;
  }

  &__grid {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 4px;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__thumb {
    position: relative;
    flex-shrink: 0;
    width: 72px;
    height: 72px;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    border: 2px solid rgba(255, 255, 255, 0.1);
    transition: border-color 0.2s;

    &:hover {
      border-color: rgba(100, 180, 255, 0.5);
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__thumb-remove {
    position: absolute;
    top: 2px;
    right: 2px;
    background: rgba(0, 0, 0, 0.6) !important;
  }
}
</style>
